import {
    ArrowDownIcon,
    ArrowPathIcon,
    ArrowUpIcon,
    PlusIcon,
    SparklesIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import {
    buildPercentColoursFromStops,
    DEFAULT_GRADING_STOPS,
    loadGradingStops,
    randomGradingStops,
    setGradingStops,
} from "../components/filters/PercentColours";
import {
    CLIP_LEAD_TIME_KEY,
    DEFAULT_CLIP_LEAD_TIME,
    getClipLeadTime,
} from "../utils/utils";

const DEFAULT_NOTES_CATEGORIES = [
  "Overview",
  "Serving",
  "Playing Style",
  "Mental Game",
  "Physicality",
  "Other",
];
const NOTES_KEY = "psts_notes_categories";

const THEMES = [
  "light", "dark", "cupcake", "bumblebee", "emerald", "corporate", "synthwave",
  "retro", "cyberpunk", "valentine", "halloween", "garden", "forest", "aqua",
  "lofi", "pastel", "fantasy", "wireframe", "black", "luxury", "dracula",
  "cmyk", "autumn", "business", "acid", "lemonade", "night", "coffee", "winter",
];

const TABS = [
  { id: "appearance", label: "Appearance" },
  { id: "grading", label: "Grading Colours" },
  { id: "notes", label: "Notes Categories" },
  { id: "playback", label: "Video Playback" },
];

function loadCategories() {
  return JSON.parse(localStorage.getItem(NOTES_KEY) ?? "null") ?? [...DEFAULT_NOTES_CATEGORIES];
}

export default function Settings({ onThemeChange }) {
  const [activeTab, setActiveTab] = useState("appearance");

  // ── Notes categories ─────────────────────────────────────────────────────
  const [categories, setCategories] = useState(loadCategories);
  const [newCat, setNewCat] = useState("");

  const persistCats = (cats) => {
    setCategories(cats);
    localStorage.setItem(NOTES_KEY, JSON.stringify(cats));
  };
  const addCategory = () => {
    const trimmed = newCat.trim();
    if (!trimmed || categories.includes(trimmed)) return;
    persistCats([...categories, trimmed]);
    setNewCat("");
  };
  const deleteCategory = (idx) => persistCats(categories.filter((_, i) => i !== idx));
  const moveCat = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= categories.length) return;
    const arr = [...categories];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    persistCats(arr);
  };
  const resetCats = () => persistCats([...DEFAULT_NOTES_CATEGORIES]);

  // ── Theme ────────────────────────────────────────────────────────────────
  const [theme, setTheme] = useState(localStorage.getItem("theme") ?? "garden");
  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.querySelector("html").setAttribute("data-theme", theme);
    // Notify the top-level <Theme> wrapper so the app margins recolour live.
    window.dispatchEvent(new CustomEvent("psts-theme-change", { detail: theme }));
    if (onThemeChange) onThemeChange(theme);
  }, [theme, onThemeChange]);

  // ── Grading colour stops (11 entries: 0%, 10% … 100%) ────────────────────
  const [stops, setStops] = useState(() => loadGradingStops());

  const updateStop = (i, val) => {
    const arr = [...stops];
    arr[i] = val.toUpperCase();
    setStops(arr);
    setGradingStops(arr);
  };
  const resetStops = () => {
    const def = [...DEFAULT_GRADING_STOPS];
    setStops(def);
    setGradingStops(def);
  };
  const randomStops = () => {
    const r = randomGradingStops();
    setStops(r);
    setGradingStops(r);
  };

  // ── Clip lead time ───────────────────────────────────────────────────────
  const [leadTime, setLeadTime] = useState(() => getClipLeadTime());
  const updateLeadTime = (val) => {
    const n = Math.max(0, Number(val));
    if (!Number.isFinite(n)) return;
    setLeadTime(n);
    localStorage.setItem(CLIP_LEAD_TIME_KEY, String(n));
  };
  const resetLeadTime = () => updateLeadTime(DEFAULT_CLIP_LEAD_TIME);

  // Live preview: render the interpolated 150-colour gradient as a strip.
  const previewColours = buildPercentColoursFromStops(stops);

  return (
    <div className="flex flex-col h-full overflow-y-auto p-6 gap-6">
      <h2 className="text-lg font-bold">Settings</h2>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div role="tablist" className="tabs tabs-bordered w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            className={`tab ${activeTab === t.id ? "tab-active font-semibold" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Appearance ────────────────────────────────────────────────────── */}
      {activeTab === "appearance" && (
        <section>
          <div className="text-sm font-semibold mb-1">App Theme</div>
          <p className="text-xs text-base-content/50 mb-3">
            Current theme: <span className="font-mono">{theme}</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 max-w-5xl">
            {THEMES.map((th) => (
              <button
                key={th}
                onClick={() => setTheme(th)}
                className={`rounded-lg border-2 overflow-hidden text-xs font-medium ${
                  theme === th ? "border-primary" : "border-base-300 hover:border-primary/50"
                }`}
                data-theme={th}
                type="button"
              >
                <div className="bg-base-100 text-base-content p-2 flex flex-col gap-1.5">
                  <span className="truncate">{th}</span>
                  <div className="flex gap-1">
                    <span className="w-4 h-4 rounded bg-primary" />
                    <span className="w-4 h-4 rounded bg-secondary" />
                    <span className="w-4 h-4 rounded bg-accent" />
                    <span className="w-4 h-4 rounded bg-neutral" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── Grading colours ───────────────────────────────────────────────── */}
      {activeTab === "grading" && (
        <section className="max-w-3xl">
          <div className="text-sm font-semibold mb-1">Heatmap Grading Colours</div>
          <p className="text-xs text-base-content/50 mb-3">
            Define a colour for each 10% step (0%, 10%, …, 100%). Intermediate
            values are interpolated linearly. Used by the Stats Analysis Heatmap,
            Zone Count and Zone Percentage displays.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            {stops.map((c, i) => (
              <label key={i} className="flex flex-col items-center gap-1">
                <span className="text-xs font-mono text-base-content/60">{i * 10}%</span>
                <input
                  type="color"
                  value={c}
                  onChange={(e) => updateStop(i, e.target.value)}
                  className="w-12 h-12 rounded cursor-pointer border border-base-300"
                />
                <span className="text-[10px] font-mono">{c}</span>
              </label>
            ))}
          </div>

          <div className="mb-4">
            <div className="text-xs text-base-content/60 mb-1">Preview</div>
            <div className="flex h-8 rounded overflow-hidden border border-base-300">
              {previewColours.map((c, i) => (
                <div key={i} className="flex-1" style={{ background: c }} />
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button className="btn btn-sm btn-primary" onClick={randomStops} type="button">
              <SparklesIcon className="w-4 h-4" />
              Random scheme
            </button>
            <button className="btn btn-sm btn-ghost" onClick={resetStops} type="button">
              <ArrowPathIcon className="w-4 h-4" />
              Reset to default
            </button>
          </div>
        </section>
      )}

      {/* ── Notes categories ─────────────────────────────────────────────── */}
      {activeTab === "notes" && (
        <section className="max-w-md">
          <div className="text-sm font-semibold mb-1">Notes Categories</div>
          <p className="text-xs text-base-content/50 mb-3">
            Categories used in the player Notes panel. Changes take effect the next time you open a
            player.
          </p>

          <div className="flex flex-col gap-1 mb-3">
            {categories.map((cat, i) => (
              <div key={cat} className="flex items-center gap-1 bg-base-200 rounded px-3 py-1.5">
                <span className="flex-1 text-sm">{cat}</span>
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => moveCat(i, -1)}
                  disabled={i === 0}
                  title="Move up"
                >
                  <ArrowUpIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => moveCat(i, 1)}
                  disabled={i === categories.length - 1}
                  title="Move down"
                >
                  <ArrowDownIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  className="btn btn-ghost btn-xs text-error"
                  onClick={() => deleteCategory(i)}
                  title="Delete"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input input-bordered input-sm flex-1"
              placeholder="New category name…"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCategory();
              }}
            />
            <button
              className="btn btn-sm btn-primary"
              onClick={addCategory}
              disabled={!newCat.trim() || categories.includes(newCat.trim())}
            >
              <PlusIcon className="w-4 h-4" />
              Add
            </button>
          </div>

          <button className="btn btn-ghost btn-sm" onClick={resetCats}>
            Reset categories to defaults
          </button>
        </section>
      )}

      {/* ── Video playback ───────────────────────────────────────────────── */}
      {activeTab === "playback" && (
        <section className="max-w-md">
          <div className="text-sm font-semibold mb-1">Clip Leading Time</div>
          <p className="text-xs text-base-content/50 mb-3">
            Number of seconds to start a clip <em>before</em> an event when viewing
            event clips, so you see the lead-up to each shot.
          </p>

          <label className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              step="0.5"
              className="input input-bordered input-sm w-28"
              value={leadTime}
              onChange={(e) => updateLeadTime(e.target.value)}
            />
            <span className="text-sm text-base-content/70">seconds</span>
            <button className="btn btn-ghost btn-sm" onClick={resetLeadTime} type="button">
              <ArrowPathIcon className="w-4 h-4" />
              Reset
            </button>
          </label>
        </section>
      )}
    </div>
  );
}
